import styled from "@emotion/styled";
import { ReactNode, useCallback, useLayoutEffect, useRef, useState } from "react";

import { MDSTooltip, MDSTooltipPlacement, MDSTooltipTrigger } from "@/mds/components/tooltip";

interface ReminderShillOverflowTooltipTextProps {
  children: ReactNode;
  className?: string;
  label: ReactNode;
  text: string;
}

export const REMINDER_SHILL_OVERFLOW_TEXT_ATTRIBUTE = "data-reminder-shill-overflow-text";

/**
 * Shows the full reminder shill copy only when the rendered text is clipped by ellipsis.
 */
export function ReminderShillOverflowTooltipText({
  children,
  className,
  label,
  text,
}: ReminderShillOverflowTooltipTextProps) {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const getTextElement = useCallback(() => {
    return contentRef.current?.querySelector<HTMLElement>(
      `[${REMINDER_SHILL_OVERFLOW_TEXT_ATTRIBUTE}="true"]`
    );
  }, []);

  const updateOverflowState = useCallback(() => {
    const textElement = getTextElement();
    if (!textElement) {
      setIsOverflowing(false);
      return;
    }

    setIsOverflowing(textElement.scrollWidth > textElement.clientWidth + 1);
  }, [getTextElement]);

  useLayoutEffect(() => {
    updateOverflowState();
    const animationFrame = requestAnimationFrame(updateOverflowState);

    const textElement = getTextElement();
    if (!textElement) {
      cancelAnimationFrame(animationFrame);
      return;
    }

    const resizeObserver = new ResizeObserver(updateOverflowState);
    resizeObserver.observe(contentRef.current ?? textElement);
    resizeObserver.observe(textElement);
    window.addEventListener("resize", updateOverflowState);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateOverflowState);
    };
  }, [getTextElement, text, updateOverflowState]);

  return (
    <MDSTooltip
      config={{
        label,
        placement: MDSTooltipPlacement.Top,
      }}
      isDisabled={!isOverflowing}
    >
      <StyledTooltipTrigger className={className} tabIndex={isOverflowing ? 0 : -1}>
        <StyledContent ref={contentRef}>{children}</StyledContent>
      </StyledTooltipTrigger>
    </MDSTooltip>
  );
}

const StyledTooltipTrigger = styled(MDSTooltipTrigger)`
  min-width: 0;
`;

const StyledContent = styled.div`
  display: flex;
  min-width: 0;
  width: 100%;
`;
